import b from 'b_';
import React, { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import ConsoleStore from '../../stores/ConsoleStore';
import SnackStore from '../../stores/SnackStore';
import UDPoliciesStore from '../../stores/UDPolicies/UDPoliciesStore';
import Accordion from '../Accordion/Accordion';
import CategoryLookup from '../CategoryLookup';
import FuseConfirmationModal from '../ConfirmationModal/FuseConfirmationModal';
import AddEditUrlModal from '../ContentFiltering/AddEditUrlModal';
import ContentFiltering from '../ContentFiltering/ContentFiltering';
import MoreMenu from '../ContentFiltering/FilterByUrl';
import DataLossPrevention from '../DataLossPrevention/DataLossPrevention';
import Button from '../Button';
import {
    ConsoleCard,
    ConsoleCardTitle,
    ConsoleField,
    ConsoleList,
    ConsoleListAction,
} from '../ConsoleFragments';
import ConsoleModal from '../ConsoleModal/ConsoleModal';
import FuseInfoTooltip from '../FuseControls/FuseTooltip/FuseInfoTooltip';
import Icon from '../Icon/Icon';
import { ObservableTextfield } from '../Textfield';
import { ObservableToggle } from '../Toggle/Toggle';
import { ObservableSelect } from '../Select/Select';
import UserGroups from './UserGroups';
import UrlBulkEditorCard from '../BulkEditor/UrlBulkEditorCard';
import './UDPolicyModal.css';


const block = b.lock('UDPolicyModal');

// in case policy name is empty string (e.g in story or on DEV)
const getPolicyDisplayName = (p) => (p && p.name ? `Policy “${p.name}”` : 'Policy');

class UDPolicyModal extends Component {
    static propTypes = {
        console: ConsoleStore.PropType.isRequired,
        udPolicies: UDPoliciesStore.PropType.isRequired,
        snack: SnackStore.PropType.isRequired,
    };

    onCreate = (event) => {
        event.preventDefault();
        this.props.udPolicies.createPolicy()
            .then((p) => p && this.props.snack.showSuccess(`${getPolicyDisplayName(p)} has been added successfully.`))
            .catch((e) => e && this.props.snack.showError(`Failed to add policy. ${e.message}`));
    };

    onUpdate = (event) => {
        event.preventDefault();
        this.props.udPolicies.updatePolicy()
            .then((p) => p && this.props.snack.showSuccess(`${getPolicyDisplayName(p)} has been updated successfully.`))
            .catch((e) => e && this.props.snack.showError(`Failed to update policy. ${e.message}`));
    };

    getNameErrorMessage = () => {
        const { nameStore, isDefault } = this.props.udPolicies.editStore;
        if (!isDefault && !nameStore.isTouched && nameStore.isEmpty) {
            return 'Policy name is required';
        }
    };

    renderPolicyDefinition() {
        const { editStore } = this.props.udPolicies;
        return <Fragment>
            {editStore.isDefault ? <div className={block('PolicyName')}>
                <div className={block('FieldName')}>Policy Name</div>
                <div className={block('FieldValue')}>
                    <strong>Default Policy</strong>
                </div>
            </div> : <ObservableTextfield
                className={block('PolicyName')}
                label="Policy Name"
                store={editStore.nameStore}
                errorLabel={this.getNameErrorMessage()}
            />}
            <ConsoleField
                className={block('RedirectStatus')}
                name="Redirect Status"
                info="This option controls the redirection of users based on the policy settings.
                              Disable to prevent any redirection."
            >
                <ObservableToggle
                    onLabel="ENABLED"
                    offLabel="DISABLED"
                    store={editStore.enabledStore}
                />
            </ConsoleField>
        </Fragment>;
    }

    renderRedirectByCategory() {
        const { editStore } = this.props.udPolicies;
        return <ConsoleCard>
            <ConsoleCardTitle>
                REDIRECT BY CATEGORY
                <FuseInfoTooltip inline>
                    Choose specific web categories to redirect when a URL is clicked in corporate email.
                </FuseInfoTooltip>
            </ConsoleCardTitle>
            <CategoryLookup
                className={block('FilterRedirectOptionsInput')}
                placeholder="Search by Name or Domain"
                inputStore={editStore.isolatedCategoriesFilterStore}
                searchResultStore={editStore.searchedDomainCategoryIds}
            />
            <div className={block('RedirectCategoriesRow')}>
                <div className={block('RedirectCategoriesColumn')}>
                    <ConsoleList
                        className={block('RedirectCategories')}
                        title={`Categories (${editStore.unselectedIsolatedCategoriesCount})`}
                    >
                        {editStore.unselectedIsolatedCategories.map(({ id, name }) => <Fragment key={id}>
                            { editStore.isNamedSetCategory({ id, name }) ?
                                <Icon className={block('ListItemIcon')} type="warning" /> : null}
                            <div className={block('ListItem', { left: true })}>{name}</div>
                            <ConsoleListAction
                                className={block('RedirectCategory')}
                                onClick={() => editStore.isolatedCategories.push(id)}
                            >{'Redirect >'}</ConsoleListAction>
                        </Fragment>)}
                    </ConsoleList>
                    <ConsoleListAction
                        className={block('RedirectAllCategories')}
                        global
                        onClick={editStore.redirectAllCategories}
                    >{'REDIRECT ALL >>'}</ConsoleListAction>
                </div>
                <div className={block('RedirectCategoriesColumn')}>
                    <ConsoleList
                        className={block('RedirectCategories')}
                        title={`Categories to Redirect (${editStore.selectedIsolatedCategoriesCount})`}
                    >
                        {editStore.selectedIsolatedCategories.map(({ id, name }) => <Fragment key={id}>
                            <ConsoleListAction
                                className={block('RemoveCategory')}
                                onClick={() => editStore.isolatedCategories.remove(id)}
                            >{'< Remove'}</ConsoleListAction>
                            { editStore.isNamedSetCategory({ id, name }) ?
                                <Icon className={block('ListItemIcon')} type="warning" /> : null }
                            <div className={block('ListItem', { right: true })}>{name}</div>
                        </Fragment>)}
                    </ConsoleList>
                    <ConsoleListAction
                        className={block('RemoveAllCategories')}
                        global
                        onClick={editStore.removeAllCategories}
                    >{'<< REMOVE ALL'}</ConsoleListAction>
                </div>
            </div>
        </ConsoleCard>;
    }

    renderUrlPatternList(title, urlModalInputLabel, urlListStore) {
        return <Fragment>
            <ConsoleList
                className={block('List')}
                title={<Fragment>
                    {title} ({urlListStore.count})
                    <Button
                        primary
                        raised
                        icon="add"
                        onClick={urlListStore.addNewUrl}
                        className={block('HeaderButton')}
                    >NEW URL</Button>
                </Fragment>}
                focusedItemIndex={urlListStore.editedItemIdx || urlListStore.menu.id}
            >
                {urlListStore.filtered.map((url, index) => <Fragment key={`${index}_${url}`}>
                    <div
                        className={block('ListItem', { left: true })}
                        title={url}
                    >{url}</div>
                    <MoreMenu
                        menuId={index}
                        menuStore={urlListStore.menu}
                    >
                        <MoreMenu.Item
                            icon={'edit'}
                            onClick={() => urlListStore.editInModal(url)}
                        >Edit URL…</MoreMenu.Item>
                        <MoreMenu.Item
                            icon={'delete'}
                            onClick={() => urlListStore.removeFromList(url)}
                        >Delete URL</MoreMenu.Item>
                    </MoreMenu>
                </Fragment>)}
            </ConsoleList>

            <AddEditUrlModal
                inputLabel={urlModalInputLabel}
                urlList={urlListStore}
            />

        </Fragment>;
    }

    renderRedirectExceptions() {
        const { editStore } = this.props.udPolicies;
        return <ConsoleCard>
            <ConsoleCardTitle>
                REDIRECT EXCEPTIONS
                <FuseInfoTooltip inline>
                    Specify individual URLs to never redirect to Isolation when a web page is requested.
                </FuseInfoTooltip>
            </ConsoleCardTitle>
            <div style={{ padding: '0 20px' }}>
                <UrlBulkEditorCard
                    title={'Never Isolate URLs'}
                    subTitle={'Enter URL patterns below (1 entry per line)'}
                    validUrlText={'Never Isolate Entries'}
                    errorMassage={'Invalid URL pattern'}
                    placeholder="Enter or paste URL patterns"
                    store={editStore.neverIsolate}
                    legacyGuidelines
                />
            </div>
        </ConsoleCard>;
    }

    renderSecurityOptions() {
        const { editStore } = this.props.udPolicies;
        return <Fragment>
            <ConsoleField
                className={block('AllowExit')}
                name="Allow users to “Exit Isolation”"
                info="Display “Exit Isolation” option to users
                              if no threat was detected after a Proofpoint scan."
            >
                <ObservableToggle
                    onLabel="ENABLED"
                    offLabel="DISABLED"
                    store={editStore.allowExitIsolationFromSafeStateStore}
                />
            </ConsoleField>
            <Fragment>
                <ConsoleField
                    className={block('ExitConfirmation')}
                    name="Display exit confirmation"
                    disabled={!editStore.allowExitIsolationFromSafeStateStore.isTrue}
                >
                    <ObservableToggle
                        onLabel="ENABLED"
                        offLabel="DISABLED"
                        store={editStore.displayExitWarningStore}
                        disabled={!editStore.allowExitIsolationFromSafeStateStore.isTrue}
                    />
                </ConsoleField>
                <ObservableTextfield
                    className={block('ExitConfirmationText')}
                    label="Confirmation message"
                    store={editStore.exitWarningTextStore}
                    disabled={!editStore.allowExitIsolationFromSafeStateStore.isTrue
                        || !editStore.displayExitWarningStore.isTrue}
                    errorLabel={this.getNameErrorMessage()}
                    placeholder="By exiting, the URL that first opened in Isolation will display in your native browser.
                    You will no longer have enhanced malware and phishing protection."
                    multiLine
                    fullWidth
                />
            </Fragment>
        </Fragment>;
    }

    getModalTitle(editStore) {
        if (editStore.isNew) {
            return 'Add New Policy';
        }
        if (editStore.viewMode) {
            return 'View Policy';
        }
        return 'Edit Policy';
    }

    render() {
        const { editStore, createStatus, updateStatus, lastSyncGroupDate } = this.props.udPolicies;
        return <Fragment>
            <ConsoleModal
                isOpen={editStore.modal.isOpen}
                onClose={editStore.closeModal}
                onAfterOpen={() => editStore.isNew && editStore.nameStore.focus()}
                title={this.getModalTitle(editStore)}
                style={{ content: { width: '100%', maxWidth: 1010 } }}
                contentClassName={block('ModalContent')}
                headerActions={editStore.isNew ? <Button
                    className={block('Add')}
                    style={{ minWidth: 100 }}
                    primaryGreen
                    raised
                    disabled={!editStore.isValid()}
                    loading={createStatus.isLoading}
                    onClick={this.onCreate}
                >Add</Button> : <Button
                    className={block('Save')}
                    style={{ minWidth: 100 }}
                    primaryGreen
                    raised
                    disabled={!editStore.hasChanged() || !editStore.isValid() || editStore.viewMode}
                    loading={updateStatus.isLoading}
                    onClick={this.onUpdate}
                >Save{editStore.viewMode ? ' - Disabled' : ''}</Button>}
            >
                <Accordion
                    label="Policy Definition"
                    labelClassName={block('AccordionLabel')}
                    contentClassName={block('Row')}
                    flagStore={editStore.policyDefinitionToggle}
                >{this.renderPolicyDefinition()}</Accordion>
                {editStore.isDefault ? null : <Accordion
                    label="User Groups"
                    labelClassName={block('AccordionLabel')}
                    flagStore={editStore.userGroupsToggle}
                ><UserGroups store={editStore} lastSyncGroupDate={lastSyncGroupDate} /></Accordion>}
                <Accordion
                    label="Redirect Options"
                    labelClassName={block('AccordionLabel')}
                    flagStore={editStore.redirectOptionsToggle}
                >
                    {this.renderRedirectByCategory()}
                    {this.renderRedirectExceptions()}
                </Accordion>

                <Accordion
                    label="Security Options"
                    labelClassName={block('AccordionLabel')}
                    contentClassName={block('Row', { securityOptions: true })}
                    flagStore={editStore.securityOptionsToggle}
                >{this.renderSecurityOptions()}</Accordion>

                <Accordion
                    label="Data Loss Prevention"
                    labelClassName={block('AccordionLabel')}
                    flagStore={editStore.dlpToggle}
                >
                    <DataLossPrevention dlp={editStore.dlpStore} />
                </Accordion>

                <Accordion
                    label="Content Filtering"
                    labelClassName={block('AccordionLabel')}
                    flagStore={editStore.contentFilteringToggle}
                >
                    <ContentFiltering contentFilteringStore={editStore.contentFilteringStore} />
                </Accordion>
                {this.props.console.isConsoleAdmin ? <Accordion
                    label="Proofpoint Admin Options"
                    labelClassName={block('AccordionLabel')}
                    contentClassName={block('AdminOptions')}
                    flagStore={editStore.adminOptionsToggle}
                >
                    <ConsoleField
                        className={block('AdminOption', { type: 'alwaysRedirectIgnoringScanStore' })}
                        name="Always redirect URL regardless of a recently safe scan"
                        info="Always redirect URLs based on policy even if it was recently scanned and marked as safe."
                    >
                        <ObservableToggle
                            onLabel="ENABLED"
                            offLabel="DISABLED"
                            store={editStore.alwaysRedirectIgnoringScanStore}
                        />
                    </ConsoleField>
                    {this.props.console.isSuperAdmin ? <ConsoleField
                        className={block('AdminOption', { type: 'scanTimeoutStore' })}
                        name="Specify a timeout while waiting for a full scan"
                        info="Assume the URL is safe if the scan result is not returned within this time."
                    >
                        <ObservableSelect optionsTop store={editStore.scanTimeoutStore} />
                    </ConsoleField> : null}
                    <ConsoleField
                        className={block('AdminOption', { type: 'allowInputAfterTStaticSafeScanStore' })}
                        name="Process Never Isolate Entries After Redirect"
                        info="Apply never-isolate to redirects that happen in server browser."
                    >
                        <ObservableToggle
                            onLabel="ENABLE"
                            offLabel="DISABLE"
                            store={editStore.neverIsolateAfterRedirectStore}
                        />
                    </ConsoleField>
                </Accordion> : null}
            </ConsoleModal>

            <FuseConfirmationModal
                store={editStore.exitConfirmationModal}
                onConfirm={editStore.modal.close}
            />
        </Fragment>;
    }
}

export default inject('console', 'udPolicies', 'snack')(observer(UDPolicyModal));
